import {
    Dispatch,
    FocusEvent,
    SetStateAction,
    useEffect,
    useState,
    useRef,
} from 'react';
import { Validation } from './validator.utils';
import { ManageTemplateInstance } from '../components/ui/ManageTemplate';
import { useNotification } from '../components/providers/NotificationProvider';
import { useApolloClient } from '@apollo/react-hooks';
import { DocumentNode } from 'graphql';
import { AlertMessage } from '../shared/types';
import { useHistory, useParams } from 'react-router-dom';
import { translateErrorMessage } from '../shared/error-handling';
import ApolloClient from 'apollo-client';
import React from 'react';
import { isPremiumChannelSubscription } from './channel.utils';

export interface FormGroup {
    value: any;
    isValid: boolean;
    getIsValid: () => boolean;
    setDirty: () => void;
    isChanged: boolean;
}

export interface FormControl<T> {
    initialValue: T;
    value: T;
    setValue: Function;
    setDirty: Function;
    setInitialValue: Function;
    handleBlur: (event: FocusEvent<any>) => void;
    validationMessage: string;
    isValid: boolean;
    getIsValid: () => boolean;
    showError: boolean;
    isChanged: boolean;
    setValidators: (newValidations: validatorFunc[]) => void;
}

export interface FormGroupValue {
    [key: string]: FormControl<any>;
}

export interface ManageTemplateForm {
    isNew: boolean;
    formMessage: AlertMessage;
    updateFormMessage: (msg: string) => void;
    formBusy: boolean;
    onSubmit: (ev: any, body: any) => void | Promise<void>;
    onCancel: () => void;
    onDelete: (item: string) => Promise<void>;
    onAlertBoxClose: () => void;
}

export interface ConstrainTextArea {
    charsLimit: number;
    charsMessage: string;
    setCurrentChars: Dispatch<SetStateAction<number>>;
    currentChars: number;
}

export enum FormSnackBarType {
    Added = 'added',
    Subscribed = 'subscribed',
    Updated = 'updated',
    Deleted = 'deleted',
    Archived = 'archived',
    Published = 'published',
}

// @ts-ignore
export const generateSnackBarFormMessage = (
    templateInstance: ManageTemplateInstance,
    name: string | null,
    type: FormSnackBarType,
    item?: any
): JSX.Element => {
    switch (templateInstance) {
        case ManageTemplateInstance.Subscription:
            return (
                <span>
                    You are now {type} to {name}. Change your subscriptions and
                    email preferences at any time.
                </span>
            );
        case ManageTemplateInstance.PromotionPage:
            switch (type) {
                case FormSnackBarType.Added:
                    return (
                        <span>
                            The landing page <strong>{name}</strong> has been
                            created and selected.
                        </span>
                    );
                case FormSnackBarType.Updated:
                    return (
                        <span>
                            The landing page <strong>{name}</strong> has been
                            updated.
                        </span>
                    );
                case FormSnackBarType.Deleted:
                    return <span>The landing page has been removed.</span>;
                default:
                    return (
                        <span>
                            The landing page <strong>{name}</strong> has been{' '}
                            {type}.
                        </span>
                    );
            }
        case ManageTemplateInstance.Channel:
            switch (type) {
                case FormSnackBarType.Added:
                    return (
                        <span>
                            The {isPremiumChannelSubscription(item.type)}
                            <strong> {name}</strong> has been created.
                        </span>
                    );
                case FormSnackBarType.Updated:
                    return (
                        <span>
                            The {isPremiumChannelSubscription(item.type)}
                            <strong> {name}</strong> has been updated.
                        </span>
                    );
                default:
                    return (
                        <span>
                            The {isPremiumChannelSubscription(item?.type)}
                            <strong> {name}</strong> has been {type}.
                        </span>
                    );
            }
        default:
            return (
                <span>
                    The {templateInstance} <strong>{name}</strong> has been{' '}
                    {type}.
                </span>
            );
    }
};

export const useFormGroup = <T extends FormGroupValue>(
    fg: T
): FormGroup & T => {
    const controls = Object.values(fg);
    const value = Object.fromEntries(
        Object.entries(fg).map(([key, formControl]) => {
            return [
                [key],
                formControl?.value?.value ?? formControl?.value ?? undefined,
            ];
        })
    );

    const isChanged: boolean = (controls ?? []).some((c) => c.isChanged);
    const getIsValid = () => (controls ?? []).every((c) => c.getIsValid());

    const setDirty = (): any => {
        controls.forEach((c: FormControl<any>) => {
            c.setDirty(true);
        });
    };

    return {
        ...fg,
        value,
        isValid: getIsValid(),
        getIsValid,
        setDirty,
        isChanged,
    };
};

type validatorFunc = (val: any) => Validation;

export const useFormControl = <T,>(
    val: T,
    initialValidators?: validatorFunc[]
): FormControl<T> => {
    const [initialValue, setInitialValue] = useState<T>(val);
    const [value, setValue] = useState<T>(val);
    const [isDirty, setDirty] = useState<boolean>(false);
    const [validationMessage, setValidationMessage] = useState('');
    const [, setTriggerValidation] = useState(0);

    const validators = useRef(initialValidators);

    const isChanged = !(initialValue === value);

    useEffect(() => {
        setValidationMessage('');

        for (const validator of validators.current ?? []) {
            const validation: Validation = validator(value);
            if (validation?.valid) {
                continue;
            }
            const message = validation?.message;
            if (message) {
                setValidationMessage(message);
                break;
            }
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value, isDirty, validators.current]);

    const isValid = () =>
        validators.current?.every((v) => v(value)?.valid) ?? true;
    const showError = isDirty && !isValid();
    const setValidators = (newValidators: validatorFunc[]) => {
        validators.current = newValidators;
        setTriggerValidation((x) => x + 1);
    };

    const handleBlur = (): void => setDirty(true);

    return {
        value,
        setValue,
        setDirty,
        handleBlur,
        validationMessage,
        isValid: isValid(),
        getIsValid: isValid,
        showError,
        setInitialValue: (val: T) => {
            setValue(val);
            setInitialValue(val);
        },
        initialValue,
        isChanged,
        setValidators,
    };
};

export const useConstrainTextArea = (
    initialChars = 0,
    charsLimit = 500
): ConstrainTextArea => {
    const [currentChars, setCurrentChars] = useState(initialChars);
    const [charsMessage, setCharsMessage] = useState('');

    useEffect(() => {
        const calculateCharsLeft = (): number =>
            +(charsLimit - +currentChars).toFixed();
        const charsLeftMessage = (): string =>
            `${calculateCharsLeft()} characters remaining`;

        setCharsMessage(charsLeftMessage());
    }, [currentChars, charsLimit]);

    return { charsLimit, charsMessage, setCurrentChars, currentChars };
};

export const useManageTemplateForm = <T,>(
    templateInstance: ManageTemplateInstance,
    form: FormGroup,
    editItem: T,
    addMutation: DocumentNode | null,
    editMutation: DocumentNode | null,
    deleteMutation: DocumentNode | null,
    returnListUrl: string,
    documentId?: string
): ManageTemplateForm => {
    const urlParams = useParams<{ _id: string }>();
    const _id = documentId ?? urlParams._id;
    const history = useHistory();
    const { showMessage } = useNotification();
    const [formMessage, setFormMessage] = useState<AlertMessage>({
        style: 'info',
        message: null,
    });
    const [formBusy, setFormBusy] = useState<boolean>(false);
    const apolloClient: ApolloClient<object> = useApolloClient();

    const isNew = !editItem;

    const goBack = () => {
        history.push(returnListUrl);
    };

    let busyTimeout: NodeJS.Timeout;

    const onSubmit = async (ev: any, body: any): Promise<void> => {
        ev?.preventDefault();
        form.setDirty();
        if (!form.isValid) {
            return;
        }
        if (!form.isChanged) {
            goBack();
            return;
        }
        await (isNew ? add(body) : edit(body));
    };

    const onCancel = (): void => {
        goBack();
    };

    function updateFormMessage(msg: string) {
        setFormMessage({
            style: 'error',
            message: translateErrorMessage(msg),
        });
    }

    const onAlertBoxClose = (): void => {
        setFormMessage({
            style: 'info',
            message: null,
        });
    };

    const setBusy = (busy: boolean) => {
        clearTimeout(busyTimeout);
        if (!busy) {
            setFormBusy(false);
            return;
        }

        busyTimeout = setTimeout(() => setFormBusy(true), 2000);
    };

    const add = async (item: any): Promise<void> => {
        if (!addMutation) {
            return;
        }
        try {
            setBusy(true);

            const trimmedName = (getItemName(item) || '').trim();

            await apolloClient.mutate({
                mutation: addMutation as DocumentNode,
                variables: {
                    item: {
                        ...item,
                        ...(trimmedName ? { name: trimmedName } : {}),
                    },
                },
            });

            showMessage(
                generateSnackBarFormMessage(
                    templateInstance,
                    trimmedName,
                    FormSnackBarType.Added,
                    item
                )
            );
            goBack();
        } catch (error) {
            console.error(error.message);
            setBusy(false);
            setFormMessage({
                style: 'error',
                message: translateErrorMessage(error.message),
            });
        }
    };

    const edit = async (item: any): Promise<void> => {
        if (!editItem) {
            return;
        }
        try {
            setBusy(true);

            const trimmedName = (getItemName(item) || '').trim();

            await apolloClient.mutate({
                mutation: editMutation as DocumentNode,
                variables: {
                    item: {
                        _id,
                        ...item,
                        ...(trimmedName ? { name: trimmedName } : {}),
                    },
                },
            });

            showMessage(
                generateSnackBarFormMessage(
                    templateInstance,
                    trimmedName,
                    FormSnackBarType.Updated,
                    item
                )
            );
            goBack();
        } catch (error) {
            console.error(error.message);
            setBusy(false);
            setFormMessage({
                style: 'error',
                message: translateErrorMessage(error.message),
            });
        }
    };

    const onDelete = async (name: string): Promise<void> => {
        if (!deleteMutation) {
            return;
        }
        try {
            setBusy(true);

            await apolloClient.mutate({
                mutation: deleteMutation as DocumentNode,
                variables: { id: _id },
            });

            showMessage(
                generateSnackBarFormMessage(
                    templateInstance,
                    name,
                    FormSnackBarType.Deleted
                )
            );
            goBack();
        } catch (error) {
            console.error(error.message);
            setBusy(false);
            setFormMessage({
                style: 'error',
                message: translateErrorMessage(error.message),
            });
        }
    };

    const getItemName = (item: any): string =>
        item[Object.keys(item).find((key: string) => key === 'name') as string];

    return {
        isNew,
        formMessage,
        updateFormMessage,
        formBusy,
        onSubmit,
        onCancel,
        onDelete,
        onAlertBoxClose,
    };
};
